'use client';

import {
  BusinessSubCategory,
  IBusinessSubCategory,
  IBusinessSubCategoryData,
} from "@order-up/models";
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { DocumentWithId } from "../interfaces/FirestoreRepository";
import { businessSubCategoryService } from "../services/businessSubCategories";
import { createLogger } from "../utils/logger";

const logger = createLogger("useBusinessSubCategories");

const businessSubCategoryKeys = {
  all: ["businessSubCategories"] as const,
  lists: () => [...businessSubCategoryKeys.all, "list"] as const,
  byType: (businessTypeGuid: string) =>
    [...businessSubCategoryKeys.all, "byType", businessTypeGuid] as const,
};

/**
 * Hook to fetch all platform-wide business sub-categories.
 */
const useGetBusinessSubCategories = (): UseQueryResult<
  DocumentWithId<BusinessSubCategory>[],
  Error
> => {
  return useQuery({
    queryKey: businessSubCategoryKeys.lists(),
    queryFn: () => businessSubCategoryService.getBusinessSubCategories(),
  });
};

/**
 * Hook to fetch business sub-categories filtered by parent business type GUID.
 */
const useGetBusinessSubCategoriesByType = (
  businessTypeGuid: string,
): UseQueryResult<DocumentWithId<BusinessSubCategory>[], Error> => {
  return useQuery({
    queryKey: businessSubCategoryKeys.byType(businessTypeGuid),
    queryFn: () =>
      businessSubCategoryService.getBusinessSubCategoriesByType(businessTypeGuid),
    enabled: !!businessTypeGuid,
  });
};

type CreateBusinessSubCategoryInput = Omit<
  IBusinessSubCategoryData,
  "businessSubCategoryId" | "guid" | "createdAt" | "updatedAt"
> & { businessTypeGuid: string; name: string };

/**
 * Hook to create a business sub-category (admin only).
 */
const useCreateBusinessSubCategory = (): UseMutationResult<
  DocumentWithId<BusinessSubCategory>,
  Error,
  CreateBusinessSubCategoryInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBusinessSubCategoryInput) =>
      businessSubCategoryService.createBusinessSubCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSubCategoryKeys.all,
      });
    },
  });
};

/**
 * Hook to update a business sub-category by guid (admin only).
 *
 * Keyed on `guid` (a unique UUID) rather than the numeric
 * `businessSubCategoryId`, which is not guaranteed unique across counter
 * resets/re-seeds and could otherwise resolve to the wrong document.
 */
const useUpdateBusinessSubCategory = (): UseMutationResult<
  DocumentWithId<BusinessSubCategory>,
  Error,
  {
    guid: string;
    data: Partial<
      Omit<IBusinessSubCategory, "businessSubCategoryId" | "guid" | "createdAt">
    >;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guid,
      data,
    }: {
      guid: string;
      data: Partial<
        Omit<IBusinessSubCategory, "businessSubCategoryId" | "guid" | "createdAt">
      >;
    }) =>
      businessSubCategoryService.updateBusinessSubCategory(guid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSubCategoryKeys.all,
      });
    },
  });
};

/**
 * Hook to delete a business sub-category by guid (admin only).
 */
const useDeleteBusinessSubCategory = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (guid: string) =>
      businessSubCategoryService.deleteBusinessSubCategory(guid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSubCategoryKeys.all,
      });
    },
  });
};

/**
 * Subscription hook that listens to business_sub_categories collection in real-time.
 * Pushes updates directly into the React Query cache.
 */
const useBusinessSubCategoriesSubscription = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = businessSubCategoryService.subscribeToBusinessSubCategories(
      (subCategories) => {
        queryClient.setQueryData(businessSubCategoryKeys.lists(), subCategories);
      },
      (error) => {
        logger.warn("Business sub-categories subscription error, falling back to query", { error: error.message });
      },
    );

    return unsubscribe;
  }, [queryClient]);
};

export {
  useGetBusinessSubCategories,
  useGetBusinessSubCategoriesByType,
  useCreateBusinessSubCategory,
  useUpdateBusinessSubCategory,
  useDeleteBusinessSubCategory,
  useBusinessSubCategoriesSubscription,
  businessSubCategoryKeys,
};
